import { Link } from 'react-router-dom';
import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Scope Canvas redesign.
 * The tab is relabeled "Scope Canvas" (route key `canvas` unchanged). It mirrors the shipped
 * view-switcher pattern: an always-on Scope Summary + a section nav whose Expand-all/Collapse button
 * sits OUTSIDE the role=tablist (axe). The Brief view renders the official DHA RAPID placemat
 * (two-column, navy-headered) — problem, North Star, scope, C2, root causes, performance measurement,
 * milestones, and barriers — consuming the owning tab's derived helpers (buildDerivedBarriers /
 * buildDerivedAssumptions) rather than re-authoring arrays. Edit/Brief/History remain the section
 * labels; preserved testids: rapid-canvas-tab, canvas-save-history, canvas-accept-ai-problemStatement,
 * canvas-scope-in-input. Preserved strings: "Briefed scope", "Evidence links".
 */

export const CANVAS_SECTIONS = [
  { key: 'brief', label: 'Brief' },
  { key: 'edit', label: 'Edit' },
  { key: 'evidence', label: 'Evidence & Assumptions' },
  { key: 'history', label: 'History' },
];

export const CANVAS_DEFAULT_SECTION = 'brief';

// ---------------------------------------------------------------------------
// Section nav
// ---------------------------------------------------------------------------

export function CanvasSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="canvas-section-nav" data-testid="canvas-section-nav">
      <div className="canvas-nav-tabs" role="tablist" aria-label="Scope Canvas sections">
        {CANVAS_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`canvas-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`canvas-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`canvas-nav-chip canvas-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="canvas-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? CANVAS_DEFAULT_SECTION : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Always-on Scope Summary
// ---------------------------------------------------------------------------

export function CanvasSummaryPanel({
  hasProblem,
  hasNorthStar,
  scopeInCount,
  scopeOutCount,
  assumptionCount,
  evidenceCount,
  lastVersion,
}) {
  return (
    <section className="rapid-panel canvas-summary-panel" data-testid="canvas-summary-panel">
      <div className="canvas-summary-head">
        <div className="eyebrow">Scope Canvas</div>
        <h3 className="canvas-summary-headline">The structured RAPID placemat</h3>
        <p className="muted small">
          The Scope Canvas is the structured RAPID placemat: problem, North Star, scope,
          assumptions, evidence, and version history in one briefing-ready place.
        </p>
      </div>
      <div className="canvas-summary-stats">
        <div className="canvas-summary-stat is-primary">
          <span className="k">Scope</span>
          <span className="v" data-testid="canvas-summary-scope">
            {scopeInCount} / {scopeOutCount}
          </span>
          <span className="sub">in-scope / out-of-scope</span>
        </div>
        <div className="canvas-summary-stat">
          <span className="k">Core fields</span>
          <span className="v is-text">
            {hasProblem ? 'Problem ✓' : 'Problem —'} ·{' '}
            {hasNorthStar ? 'North Star ✓' : 'North Star —'}
          </span>
          <span className="sub">Editable, human-reviewed</span>
        </div>
        <div className="canvas-summary-stat">
          <span className="k">Assumptions</span>
          <span className="v">{assumptionCount}</span>
          <span className="sub">{evidenceCount} evidence links</span>
        </div>
        <div className="canvas-summary-stat">
          <span className="k">Last saved</span>
          <span className="v is-text" data-testid="canvas-summary-version">
            {lastVersion || 'No saved version'}
          </span>
          <span className="sub">Version history</span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Brief view — the official RAPID placemat
// ---------------------------------------------------------------------------

function PlacematCell({ title, children, span }) {
  return (
    <section className={`canvas-placemat-cell${span ? ' is-span' : ''}`}>
      <div className="canvas-placemat-cell-head">{title}</div>
      <div className="canvas-placemat-cell-body">{children}</div>
    </section>
  );
}

export function ScopePlacematView({ data }) {
  const {
    title,
    asOfDate,
    problemStatement,
    measureOfPerformance,
    scopeInItems,
    scopeOutItems,
    northStar,
    northStarTargets,
    c2,
    alignmentWithRelatedEfforts,
    assumptions,
    rootCauses,
    performanceMeasurement,
    milestones,
    barriers,
    evidenceLinks,
  } = data;
  return (
    <section className="rapid-panel canvas-brief-view" data-testid="canvas-brief-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Briefing view</div>
          <h3>{title}</h3>
        </div>
        <span className="chip outline sm">As of: {asOfDate || '—'}</span>
      </div>

      <div className="canvas-placemat" data-testid="canvas-placemat">
        <PlacematCell title="Problem Statement">
          <p>{problemStatement}</p>
          {measureOfPerformance ? (
            <ul className="rapid-inline-list canvas-mop">
              <li>
                <strong>Measure of Performance:</strong> {measureOfPerformance.measure}
              </li>
              <li>
                <strong>Target:</strong> {measureOfPerformance.target} · <strong>Actual:</strong>{' '}
                {measureOfPerformance.actual} · <strong>Gap:</strong> {measureOfPerformance.gap}
              </li>
            </ul>
          ) : null}
        </PlacematCell>

        <PlacematCell title="North Star">
          <p>{northStar}</p>
          {northStarTargets?.length ? (
            <ul className="rapid-inline-list">
              {northStarTargets.map((t) => (
                <li key={t.measure}>
                  <strong>{t.measure}:</strong> {t.from} → {t.to} by {t.by}
                </li>
              ))}
            </ul>
          ) : null}
        </PlacematCell>

        <PlacematCell title="Briefed scope">
          <div className="eyebrow">In scope</div>
          <ul className="rapid-inline-list">
            {scopeInItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="eyebrow">Out of scope</div>
          <ul className="rapid-inline-list">
            {scopeOutItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </PlacematCell>

        <PlacematCell title="Performance Measurement">
          <div className="rapid-table-scroll rapid-scroll-x" tabIndex={0}>
            <table className="table canvas-placemat-table">
              <thead>
                <tr>
                  <th>Tier / Driver</th>
                  <th>Baseline (MON YY)</th>
                  <th>Current actual</th>
                  <th>Current FY goal</th>
                  <th>Overarching goal</th>
                </tr>
              </thead>
              <tbody>
                {(performanceMeasurement || []).map((row) => (
                  <tr key={row.metricId || row.driver}>
                    <td>
                      T{row.tier} · {row.driver}
                    </td>
                    <td>{row.baselineLabel}</td>
                    <td>{row.currentActual}</td>
                    <td>{row.currentFyGoal}</td>
                    <td>
                      {row.overarchingGoal} ({row.overarchingFy})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link className="btn ghost sm" to="?tab=metrics">
            Open Metrics &amp; Value
          </Link>
        </PlacematCell>

        <PlacematCell title="C2 (Control & Command)">
          <ul className="rapid-inline-list">
            <li>
              <strong>Supported Directorate:</strong> {c2?.supportedDirectorate}
            </li>
            <li>
              <strong>Lead Action Officer:</strong> {c2?.leadActionOfficer}
            </li>
            <li>
              <strong>Stakeholders:</strong> {c2?.interestedStakeholders}
            </li>
          </ul>
          {c2?.alignmentStatus ? (
            <span className="chip outline sm">{c2.alignmentStatus}</span>
          ) : null}
        </PlacematCell>

        <PlacematCell title="Milestones">
          <div className="rapid-table-scroll rapid-scroll-x" tabIndex={0}>
            <table className="table canvas-placemat-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Start</th>
                  <th>Finish</th>
                  <th>Status</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {(milestones || []).map((m) => (
                  <tr key={m.task}>
                    <td>{m.task}</td>
                    <td>{m.start}</td>
                    <td>{m.finish}</td>
                    <td>
                      <StatusChip status={m.status}>{m.status}</StatusChip>
                    </td>
                    <td>{m.percentComplete}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PlacematCell>

        <PlacematCell title="Assumptions">
          <ul className="rapid-inline-list">
            {assumptions.map((a) => (
              <li key={a.id}>{a.text}</li>
            ))}
          </ul>
        </PlacematCell>

        <PlacematCell title="Root Causes / Priority Levers">
          <ul className="rapid-record-list">
            {(rootCauses || []).map((rc) => (
              <li key={rc.id}>
                <div className="muted small">
                  {rc.id} · T{rc.tier} · {rc.driverId}
                </div>
                <p>{rc.lever}</p>
              </li>
            ))}
          </ul>
        </PlacematCell>

        <PlacematCell title="Alignment with Related Efforts">
          <ul className="rapid-inline-list">
            {(alignmentWithRelatedEfforts || []).map((a) => (
              <li key={a.effort}>
                <strong>{a.effort}:</strong> {a.note}
              </li>
            ))}
          </ul>
        </PlacematCell>

        <PlacematCell title="Risks / Barrier Removal / Required Assistance">
          <ul className="rapid-record-list">
            {barriers.map((b) => (
              <li key={b.id}>
                <div className="row spread">
                  <strong>{b.id}</strong>
                  <StatusChip status={b.status}>{b.status}</StatusChip>
                </div>
                <p>{b.decisionNeeded || b.barrier}</p>
              </li>
            ))}
          </ul>
          <Link className="btn ghost sm" to="?tab=governance&gv=decisions">
            Open Governance decisions
          </Link>
        </PlacematCell>

        <PlacematCell title="Evidence links" span>
          <ul className="rapid-record-list canvas-evidence-list">
            {(evidenceLinks || []).map((link) => (
              <li key={`${link.claim}-${link.source}`}>
                <strong>{link.claim}</strong>
                <div className="muted small">
                  {link.source} · {link.type}
                </div>
                <p>{link.note}</p>
              </li>
            ))}
          </ul>
          <Link className="btn ghost sm" to="?tab=governance&gv=evidence-lineage">
            Open Evidence &amp; Lineage
          </Link>
        </PlacematCell>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit view
// ---------------------------------------------------------------------------

export function EditCanvasView({ current, expected, onUpdate, onSaveHistory }) {
  return (
    <section className="rapid-panel canvas-edit-view" data-testid="canvas-edit-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Edit canvas</div>
          <h3>Edit the placemat fields</h3>
        </div>
        <button
          className="btn ghost sm"
          data-testid="canvas-save-history"
          type="button"
          onClick={onSaveHistory}
        >
          Save history entry
        </button>
      </div>
      <label className="rapid-field">
        <span>Problem Statement</span>
        <textarea
          value={current.problemStatement}
          onChange={(event) => onUpdate({ problemStatement: event.target.value })}
          rows={5}
        />
      </label>
      <div className="rapid-inline-actions">
        <button
          className="btn ghost sm"
          data-testid="canvas-accept-ai-problemStatement"
          type="button"
          onClick={() => onUpdate({ problemStatement: expected?.problemStatement || '' })}
        >
          Apply suggested problem statement
        </button>
      </div>
      <label className="rapid-field">
        <span>North Star</span>
        <textarea
          value={current.northStar}
          onChange={(event) => onUpdate({ northStar: event.target.value })}
          rows={4}
        />
      </label>
      <div className="rapid-inline-actions">
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => onUpdate({ northStar: expected?.northStar || '' })}
        >
          Apply suggested North Star
        </button>
      </div>
      <div className="rapid-grid two-up">
        <label className="rapid-field">
          <span>Assumption</span>
          <textarea
            value={current.assumption}
            onChange={(event) => onUpdate({ assumption: event.target.value })}
            rows={4}
          />
        </label>
        <label className="rapid-field">
          <span>Milestone note</span>
          <textarea
            value={current.milestone}
            onChange={(event) => onUpdate({ milestone: event.target.value })}
            rows={4}
          />
        </label>
      </div>
      <div className="rapid-grid two-up">
        <label className="rapid-field">
          <span>Scope in</span>
          <textarea
            data-testid="canvas-scope-in-input"
            value={current.scopeInText}
            onChange={(event) => onUpdate({ scopeInText: event.target.value })}
            rows={6}
          />
        </label>
        <label className="rapid-field">
          <span>Scope out</span>
          <textarea
            value={current.scopeOutText}
            onChange={(event) => onUpdate({ scopeOutText: event.target.value })}
            rows={6}
          />
        </label>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Evidence & Assumptions view
// ---------------------------------------------------------------------------

export function EvidenceAssumptionsView({ evidenceLinks, assumptions }) {
  return (
    <section className="rapid-panel" data-testid="canvas-evidence-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Provenance</div>
          <h3>Evidence &amp; assumptions</h3>
        </div>
        <Link className="btn ghost sm" to="?tab=governance&gv=evidence-lineage">
          Open Evidence &amp; Lineage
        </Link>
      </div>
      <div className="rapid-grid two-up">
        <section>
          <div className="eyebrow">Evidence links</div>
          <ul className="rapid-record-list">
            {(evidenceLinks || []).map((link) => (
              <li key={`${link.claim}-${link.source}`}>
                <strong>{link.claim}</strong>
                <div className="muted small">
                  {link.source} · {link.type}
                </div>
                <p>{link.note}</p>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <div className="eyebrow">Assumptions &amp; constraints</div>
          <ul className="rapid-record-list">
            {assumptions.map((a) => (
              <li key={a.id}>
                <div className="muted small">{a.id}</div>
                <p>{a.text}</p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Version History view
// ---------------------------------------------------------------------------

export function VersionHistoryView({ history }) {
  return (
    <section className="rapid-panel" data-testid="canvas-history-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">History</div>
          <h3>Version history</h3>
        </div>
      </div>
      <ul className="rapid-record-list">
        {(history || []).map((entry, index) => (
          <li key={`${entry.version}-${index}`}>
            <div className="row spread">
              <strong>{entry.version}</strong>
              <span className="muted small">{entry.date}</span>
            </div>
            <p>{entry.note}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
